import { FC, useEffect, useRef, useState } from "react";
import {
  OuterContainer,
  StickyContainer,
  InnerContainer,
  Logo,
  CTA,
  LeftContainer,
  ModalBackDrop,
  RightContainer,
  LinkList,
  Link,
  Toggle,
} from './styles';
import SvgPointerDown from "../Icons/PointerDown";
import SvgX from "../Icons/X";
import { Asset, Entry } from "contentful";
import { getCloudflareImageUrl } from "../../lib/getCloudflareImageUrl";

interface StickySubNavProps extends Entry {
  id: string;
  brand: string;
  fields: {
    logo: Asset;
    ctaLabel: string;
    ctaUrl: string;
    enableTransparentBackground: boolean;
    links: any[];
    theme: string;
    mobileMenuPosition: string;
    scrollToActivate?: boolean;
  }
}

const StickySubNav: FC<StickySubNavProps> = ({ 
  id,
  brand,
  fields: {
    logo,
    links = [],
    ctaUrl,
    ctaLabel,
    enableTransparentBackground,
    theme,
    mobileMenuPosition = 'Stick To Top',
    scrollToActivate = false,
  }
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [offsetTop, setOffsetTop] = useState(0);
  const [isStuck, setIsStuck] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [fixedTopPos, setFixedTopPos] = useState(0);
  const [lastScrollY, setLastScrollY] = useState(window.scrollY);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [announcementHeight, setAnnouncementHeight] = useState(0);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
  const [searchSpringContainerHeight, setSearchSpringContainerHeight] = useState(0);
  const renderCTA = ctaUrl && ctaLabel ? true : false;
  const stickToBottom = mobileMenuPosition == 'Stick To Bottom';
  const isMobile = window.innerWidth < 993;

  useEffect(() => {
    if (containerRef && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const newOffsetTop = rect.top + window.scrollY;
      setOffsetTop(newOffsetTop);
    }

    if (headerHeight == 0) {
      getHeaderHeight();
    }
  }, [containerRef]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => { window.removeEventListener('scroll', handleScroll); }
  });

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => { window.removeEventListener('resize', handleResize); }
  });

  const handleResize = () => {
    getHeaderHeight();
  }

  const getHeaderHeight = () => {
    const header: HTMLElement|null = document.querySelector('.shopify-section-header-sticky');
    const desktopNav: HTMLElement|null = document.querySelector('.mega-menu__content');
    const announcementBar: HTMLElement|null = document.querySelector('.announcement-bar');
    const stickyHeader: HTMLElement|null = document.querySelector('sticky-header');
    const searchSpringContainer: HTMLElement|null = document.querySelector('.searchspring-search-container');

    let height = header?.offsetHeight || 0;
    if (desktopNav && !isMobile) {
      height += desktopNav.offsetHeight;
    }
    setHeaderHeight(height);

    if (announcementBar) {
      setAnnouncementHeight(announcementBar.offsetHeight);
    }

    if (stickyHeader) {
      setStickyHeaderHeight(stickyHeader.offsetHeight);
    }

    if (searchSpringContainer) {
      setSearchSpringContainerHeight(searchSpringContainer.offsetHeight);
    }
  }

  const handleScroll = () => {
    const isScrollingUp = window.scrollY < lastScrollY;

    if (headerHeight == 0) {
      getHeaderHeight();
    }
  
    // If scrollToActivate is true, only show when scrolled past primary nav
    if (scrollToActivate) {
      setIsStuck(window.scrollY > headerHeight);
    } else {
      if (
        (!isScrollingUp && window.scrollY >= offsetTop) || 
        (isScrollingUp && window.scrollY > announcementHeight)
      ) {
        setIsStuck(true);
      } else {
        setIsStuck(false);
      }
    }

    if (isScrollingUp) {
      if (isMobile) {
        // On mobile, because the header height is a fixed value,
        // the sticky nav is not guaranteed to fill that height,
        // so using headerHeight - announcementHeight can leave a gap.
        setFixedTopPos(stickyHeaderHeight);
      } else {
        setFixedTopPos(headerHeight - announcementHeight);
      }
    } else {
      setFixedTopPos(0);
    }

    setLastScrollY(window.scrollY);
  }

  const toggleMenu = () => {
    setMenuOpen(prev => !prev);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  return (
    <OuterContainer ref={containerRef} data-component-id={id} $isStuck={isStuck} $scrollToActivate={scrollToActivate}>
      {isMobile && menuOpen && <ModalBackDrop $isOpen={menuOpen} onClick={closeMenu} />}
      <StickyContainer
        $top={stickToBottom ? 'unset' : `${fixedTopPos}px`}
        $searchSpringContainerHeight={searchSpringContainerHeight}
        $bottom={stickToBottom ? '0' : 'unset'}
        $isOpen={menuOpen}
        $isStuck={isStuck}
        $stickToBottom={stickToBottom}
        $scrollToActivate={scrollToActivate}
        $transparentBackground={enableTransparentBackground}
        $brand={brand}
        $theme={theme}
      >
        <InnerContainer>
          <LeftContainer>
            {brand == 'fender' && links.length > 0 && (
              <Toggle onClick={toggleMenu} $theme={theme}>
                {menuOpen ? <SvgX fontSize="16px" /> : <SvgPointerDown fontSize="16px" />}
              </Toggle>
            )}
            <Logo>
              <img src={getCloudflareImageUrl(`https:${logo.fields.file?.url}`)} />
            </Logo>
          </LeftContainer>
          <RightContainer>
            <LinkList $isOpen={menuOpen} $includeMargin={renderCTA} $stickToBottom={stickToBottom} $brand={brand} $theme={theme}>
              {links?.map(link => (
                <Link key={link.sys.id} href={link.fields.url} $brand={brand} $theme={theme}>
                  {link.fields.label}
                </Link>
              ))}
            </LinkList>
            {brand !== 'fender' && links.length > 0 && (
              <Toggle onClick={toggleMenu} $theme={theme}>
                {menuOpen ? <SvgX fontSize="16px" /> : <SvgPointerDown fontSize="16px" />}
              </Toggle>
            )}
            {renderCTA && (
              <CTA href={ctaUrl} $brand={brand} $theme={theme}>{ctaLabel}</CTA>
            )}
          </RightContainer>
        </InnerContainer>
      </StickyContainer>
    </OuterContainer>
  );
};

export default StickySubNav;